import { defineComponent as _defineComponent } from 'vue'
import {
	onBeforeUnmount,
	onMounted,
	ref,
	watch,
} from "vue";
import {
	ChartingLibraryFeatureset,
	CreateShapeOptions,
	DatafeedConfiguration,
	EntityId,
	IBasicDataFeed,
	IChartingLibraryWidget,
	LibrarySymbolInfo,
	Mark as IMark,
	ResolutionString,
	widget as TrandingViewWidget,
} from "@/../public/chart/charting_library";
import {
	convertToHumanNumber,
	ETradeOrderMethod,
	formatPrice,
	IBar,
	IBars,
	IPnL,
	IToken,
	ITradeOrders,
	ITrades,
	RESOLUTIONS_MAP,
} from "@/shared";
import { fetchTokenEvents, fetchWalletHistoryTrades } from "@/api/endpoints";
import { useWalletStore } from "@/pinia";

interface IMarks extends Array<IMark> {}

// props
interface IProps {
	token: IToken;
	latestCandle?: IBar;
	trades: ITrades;
	orders: ITradeOrders;
	pnl: IPnL;
}

interface IEmits {
	(e: "prediction"): void;

	(e: "graph-options", opts: {}): void;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'DatafeedChart',
  props: {
    token: null,
    latestCandle: null,
    trades: null,
    orders: null,
    pnl: null
  },
  emits: ["prediction", "graph-options"],
  setup(__props: any, { expose, emit }: { emit: ({
	(e: "prediction"): void;

	(e: "graph-options", opts: {}): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
	token: IToken;
	latestCandle?: IBar;
	trades: ITrades;
	orders: ITradeOrders;
	pnl: IPnL;
};



// store
const walletStore = useWalletStore();

// data
let interval = null;
const config = {
	supports_marks: true,
	supported_resolutions: [
		"1T",
		"1S",
		"5S",
		"30S",
		"1",
		"3",
		"5",
		"15",
		"30",
		"60",
		"180",
		"240",
		"1D",
		"1W",
		"1M",
	],
} as DatafeedConfiguration;

const markHistory = ref<ITrades[]>([]);
const currentMarks = ref<IMark[]>([]);
const graph = ref<IChartingLibraryWidget | null>(null);
const updateBarsCallback = ref<Function>(() => {});
const updateMarksCallback = ref<Function>(() => {});
const arrowMarkID = ref<EntityId | null>(null);
const avgBuyID = ref<EntityId | null>(null);
const orderColors: { [i in ETradeOrderMethod]: string } = {
	[ETradeOrderMethod.LIMIT]: "dodgerblue",
	[ETradeOrderMethod.TAKE_PROFIT]: "green",
	[ETradeOrderMethod.STOP_LOSS]: "red",
	[ETradeOrderMethod.STOP_LOSS_TRAILING]: "orange",
};
const orderInitials: { [i in ETradeOrderMethod]: string } = {
	[ETradeOrderMethod.LIMIT]: "LO",
	[ETradeOrderMethod.TAKE_PROFIT]: "TP",
	[ETradeOrderMethod.STOP_LOSS]: "SL",
	[ETradeOrderMethod.STOP_LOSS_TRAILING]: "TSL",
};
const allBars = ref<IBars>([]);
const lastResolution = ref<string>("1");
const ordersID = ref<EntityId[]>([]);
const inspectorChannel = new BroadcastChannel("candle:inspector");

const chartReady = ref<boolean>(false);
const opts = Object.assign(
	{
		interval: "5",
		timeframe: "6h",
	},
	JSON.parse(localStorage.getItem("fluxbeam:chart-options") || "{}")
);

// methods
function init(): void {
	localStorage.setItem("fluxbeam:chart-options", JSON.stringify(opts));

	const disabled_features: ChartingLibraryFeatureset[] = [
		"header_symbol_search",
		"header_compare",
		// "timeframes_toolbar",
		"header_screenshot",
		"edit_buttons_in_legend",
		"symbol_search_hot_key",
		"header_undo_redo",
		"control_bar",
	];

	if (window.innerWidth < 640) {
		disabled_features.push("left_toolbar");
	}

	graph.value = new TrandingViewWidget({
		symbol: props.token?.symbol || "???",
		locale: "en",
		container: "graph",
		datafeed: getDataFeed(),
		interval: opts.interval as ResolutionString,
		timeframe: opts.timeframe,
		timezone: "exchange",
		library_path: "/chart/charting_library/",
		autosize: true,
		theme: "dark",
		loading_screen: { backgroundColor: "#000000", foregroundColor: "#FFFFFF" },
		settings_overrides: {
			"paneProperties.background": "#000000",
			"paneProperties.backgroundType": "solid",
		},
		enabled_features: [
			"show_symbol_logos",
			"seconds_resolution",
			"tick_resolution",
			"two_character_bar_marks_labels",
			"studies_extend_time_scale",
			"secondary_series_extend_time_scale",
		],
		disabled_features,
	});
	graph.value.onChartReady(() => {
		chartReady.value = true;

		const chart = graph.value.activeChart();

		let mouseDownTime = 0;

		graph.value.subscribe("mouse_down", () => {
			mouseDownTime = Date.now();
		});
		graph.value.subscribe("mouse_up", (event) => {
			const clickDuration = Date.now() - mouseDownTime;
			if (clickDuration > 100) return;

			const resolutionTime = RESOLUTIONS_MAP[lastResolution.value];
			if (!event || typeof event.clientX !== "number") return;

			const timeAtClick = chart
				.getTimeScale()
				.coordinateToTime(event.clientX - 56);

			if (timeAtClick) {
				const start = timeAtClick * 1000;
				const end = start + resolutionTime;

				inspectorChannel.postMessage({
					wallet: walletStore.address.toString(),
					mint: props.token.mint,
					start,
					end,
				});
			}
		});
	});
	graph.value.headerReady().then(() => {
		graph.value
			.activeChart()
			.onIntervalChanged()
			.subscribe(null, (interval, timeframeObj) => {
				opts.interval = interval;
				localStorage.setItem("fluxbeam:chart-options", JSON.stringify(opts));
				emit("graph-options", opts);
			});
	});
}

function getDataFeed(): IBasicDataFeed {
	return {
		onReady(callback) {
			setTimeout(() => callback(config), 0);
		},
		async searchSymbols(userInput, exchange, symbolType, onResult) {
			onResult([]);
		},
		resolveSymbol(symbolName, onResolve) {
			const [_, exponent] = props.latestCandle.close.toExponential().split("e");

			const symbolInfo = {
				base_name: ["FluxBeam"],
				ticker: props.token?.symbol || "???",
				name: props.token?.name || "Loading...",
				logo_urls: [
					`https://api.degencdn.com/v1/tokens/${props.token.address}/image.png`,
				],
				supported_resolutions: config.supported_resolutions,
				description: props.token.symbol,
				type: "crypto",
				exchange: "FluxBeam",
				session: "24x7",
				timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
				minmov: 1,
				format: "price",
				currency_code: "USD",
				pricescale: Math.pow(
					10,
					Math.abs(Number(Number(exponent) > 0 ? exponent : 6)) + 3
				),
				has_ticks: true,
				has_seconds: true,
				has_intraday: true,
				has_weekly_and_monthly: true,
				visible_plots_set: "ohlcv",
				volume_precision: 2,
				data_status: "streaming",
			} as LibrarySymbolInfo;

			setTimeout(() => onResolve(symbolInfo), 0);
		},
		async getBars(symbolInfo, resolution, periodParams, onResult, onError) {
			const bars = [] as IBars;

			const { from, to, countBack } = periodParams;
			const params = new URLSearchParams();
			params.set("interval", resolution);
			params.set("to", to.toFixed(0).toString());
			params.set("from", from.toFixed(0).toString());
			params.set("count", countBack.toString());

			const untreatBars = await (
				await fetch(
					`https://data.fluxbeam.xyz/tokens/${
						props.token.address
					}/candles?${params.toString()}`
				)
			).json();

			if (!untreatBars || untreatBars.length === 0) {
				onResult([], { noData: true });
				return;
			}

			const ival = intervalToMilliSeconds(opts.interval);
			for (let i = 0; i < untreatBars.length; i++) {
				const { open, close, high, low, volume, count, timestamp } =
					untreatBars[i];

				if (!timestamp) continue;

				//Cut off any trailing bars that have no volume so they dont interfere with the realtime update
				if (i > 1 && i == untreatBars.length - 1 && volume == 0 && count == 0) {
					continue;
				}

				bars.push({
					time: Math.floor((timestamp * 1000) / ival) * ival,
					open,
					close,
					high,
					low,
					volume,
					count,
					isBarClosed: true,
					isLastBar: open + close + high + low == 0,
				});
			}

			if (resolution !== lastResolution.value) {
				lastResolution.value = resolution;
				allBars.value.length = 0;
			}

			allBars.value.unshift(...bars);
			onResult(bars);
		},
		async subscribeBars(symbolInfo, resolution, onTick) {
			updateBarsCallback.value = onTick;
		},
		async unsubscribeBars() {},
		async getMarks(symbolInfo, from, to, onDataCallback) {
			const marks = await getHistoricTradeMarks(from, to);
			const eventMarks = await getHistoricDevTradeMarks(from, to);

			currentMarks.value = (marks || []).concat(eventMarks || []);
			onDataCallback(currentMarks.value);

			updateMarksCallback.value = onDataCallback;
		},
	};
}

async function getHistoricDevTradeMarks(from, to) {
	const events = await fetchTokenEvents({
		mint: props.token.address,
		from: from,
		to: to,
	});

	if (!events || events?.error) return;

	return _buildEventMarks(events);
}

async function getHistoricTradeMarks(from, to) {
	if (!walletStore.isConnected) return;

	const traders = await fetchWalletHistoryTrades({
		mint: props.token.address,
		wallet: walletStore.address,
		from: from,
		to: to,
	});

	if (!traders || traders?.error) return;

	const mm = {};
	for (let i = 0; i < markHistory.value.length; i++) {
		//ts-ignore
		mm[`${markHistory.value[i].time}-${markHistory.value[i].value}`] = true;
	}

	for (let i = 0; i < traders.length; i++) {
		//ts-ignore
		if (mm[`${traders[i].time}-${traders[i].value}`]) continue;
		markHistory.value.push(traders[i]);
	}

	return setTradeMarks(markHistory.value);
}

function setTradeMarks(traders) {
	const mm = {};
	for (let i = 0; i < markHistory.value.length; i++) {
		//ts-ignore
		mm[`${markHistory.value[i].time}-${markHistory.value[i].value}`] = true;
	}

	for (let i = 0; i < traders.length; i++) {
		//ts-ignore
		if (mm[`${traders[i].time}-${traders[i].value}`]) continue;
		markHistory.value.push(traders[i]);
	}
	return _buildTradeMarks(markHistory.value);
}

function _buildTradeMarks(history) {
	const marks = [] as IMarks;

	if (!history) return marks;

	for (let i = 0; i < history.length; i++) {
		const trade = history[i];
		const sell = trade.inMint === props.token.address.toString();
		const amount = sell ? trade.inAmount : trade.outAmount;

		marks.push({
			id: `${trade.value}-${trade.createdAt}`,
			time: new Date(trade.createdAt).getTime(),
			color: sell ? "red" : "green",
			label: sell ? "S" : "B",
			labelFontColor: "white",
			minSize: 16,
			text: `${sell ? "SELL" : "BUY"} ${convertToHumanNumber(amount)} ${
				props.token?.symbol || "???"
			}\n @ ${formatPrice(
				trade.value / (amount / Math.pow(10, props.token.decimals))
			)}\n = $${trade.value.toFixed(2)} ON ${new Date(
				trade.createdAt * 1000
			).toLocaleTimeString()}`,
		});
	}

	return marks;
}

function _buildEventMarks(events): IMark[] {
	const marks = [] as IMarks;

	if (!events) return marks;

	for (let i = 0; i < events.length; i++) {
		const evt = events[i];

		marks.push({
			id: `${evt.value}-${i}`,
			time: new Date(evt.createdAt).getTime(),
			color: evt.method ? "red" : "green",
			label: evt.method ? "DS" : "DB",
			labelFontColor: "white",
			minSize: 16,
			text: `${evt.method ? "DEV SELL" : "DEV BUY"} $${evt.value.toFixed(
				2
			)} - ${convertToHumanNumber(
				evt.amount / Math.pow(10, props.token?.decimals)
			)} ${props.token?.symbol || "???"}\n @ ${formatPrice(
				evt.value / (evt.amount / Math.pow(10, props.token?.decimals))
			)}`,
		});
	}

	return marks;
}

function _buildEventMarkFromTrade(trade): IMark {
	const sell = trade.inMint === props.token.address.toString();
	const amount = sell ? trade.inAmount : trade.outAmount;

	return {
		id: `${trade.value}-${trade.createdAt}`,
		time: new Date(trade.createdAt).getTime(),
		color: sell ? "red" : "green",
		label: sell ? "DS" : "DB",
		labelFontColor: "white",
		minSize: 12,
		text: `${sell ? "DEV SELL" : "DEV BUY"} $${trade.value.toFixed(
			2
		)} - ${convertToHumanNumber(
			amount / Math.pow(10, props.token?.decimals)
		)} ${props.token?.symbol || "???"}\n @ ${formatPrice(
			trade.value / (amount / Math.pow(10, props.token?.decimals))
		)}`,
	};
}

function setArrowsOnGraph(): void {
	if (arrowMarkID.value || !chartReady.value) return;
	if (!props.trades) return;

	const trade = props.trades[0];

	if (!trade) return;
	if (!props.latestCandle) return;

	const ival = intervalToMilliSeconds(opts.interval);
	const ts = Math.floor((trade.timestamp * 1000) / ival) * ival;
	if (
		props.latestCandle.count > 1 &&
		props.latestCandle.low > 0 &&
		trade.price <= props.latestCandle.low
	) {
		arrowMarkID.value = graph.value.activeChart().createShape(
			{
				//@ts-ignore
				time: ts,
				price: props.latestCandle.high,
				channel: "high",
			},
			{
				shape: "arrow_down",
			}
		);
	} else if (
		props.latestCandle.count > 1 &&
		props.latestCandle.high > 0 &&
		trade.price >= props.latestCandle.high
	) {
		arrowMarkID.value = graph.value.activeChart().createShape(
			{
				//@ts-ignore
				time: ts,
				price: props.latestCandle.low,
				channel: "low",
			},
			{
				shape: "arrow_up",
			}
		);
	}

	if (!interval) {
		interval = setInterval(() => {
			if (!graph?.value) return;
			if (!arrowMarkID.value) return;

			graph?.value?.activeChart()?.removeEntity(arrowMarkID.value);
			arrowMarkID.value = null;
		}, 400);
	}
}

function intervalToMilliSeconds(interval) {
	// If it's already a number (like 1, 5, 15, 30, 60, etc.), interpret as minutes
	if (!isNaN(interval)) {
		return parseInt(interval) * 60 * 1000;
	}

	// Handle special interval notations
	const unit = interval.slice(-1).toUpperCase();
	const value = interval.slice(0, -1);
	const numValue = value ? parseInt(value) : 1; // Default to 1 if no number specified

	switch (unit) {
		case "T": // Ticks
			return numValue * 400; // 1 tick = 400ms
		case "S": // Seconds
			return numValue * 1000;
		case "M": // Minutes
			return numValue * 60 * 1000;
		case "H": // Hours
			return numValue * 60 * 60 * 1000;
		case "D": // Days
			return numValue * 24 * 60 * 60 * 1000;
		case "W": // Weeks
			return numValue * 7 * 24 * 60 * 60 * 1000;
		default:
			console.warn(
				`Unrecognized interval format: ${interval}, defaulting to 1 minute`
			);
			return 60 * 1000; // Default to 1 minute
	}
}

function setAvgBuyPrice(): void {
	graph.value.onChartReady(() => {
		if (!props.pnl) return;

		//TODO
		const oPrice =
			props.pnl.buyValue /
			(props.pnl.buyAmount / Math.pow(10, props.pnl.decimals));

		if (avgBuyID.value) {
			graph.value.activeChart().removeEntity(avgBuyID.value);
		}

		const sh = defaultAvgBuyShape(oPrice);
		avgBuyID.value = graph.value
			.activeChart()
			.createShape({ time: Date.now(), price: oPrice }, sh);
	});
}

function setOrdersOnGraph(): void {
	graph.value.onChartReady(() => {
		for (const entityID of ordersID.value) {
			graph.value.activeChart().removeEntity(entityID);
		}
		ordersID.value.length = 0;
		for (const order of props.orders) {
			const color = orderColors[order.method];

			if (order.method === 3) {
				const oColor = order.triggerPrice > 0 ? "green" : "gray";
				const hPrice =
					order.triggerPrice > 0 ? order.triggerPrice : order.value;
				const oPrice = hPrice * (1 - order.delta / 100);

				const sh = defaultOrderShape(order, oColor, oPrice);
				sh.overrides.linewidth = 1;

				console.log("Adding TSL Shape:", {
					triggerPrice: order.triggerPrice,
					value: order.value,
					oColor,
					hPrice,
					oPrice,
				});

				ordersID.value.push(
					graph.value
						.activeChart()
						.createShape({ time: order.createdAt.getTime(), price: oPrice }, sh)
				);
				ordersID.value.push(
					graph.value
						.activeChart()
						.createShape(
							{ time: order.createdAt.getTime(), price: hPrice },
							defaultOrderShape(order, color)
						)
				);
			} else {
				ordersID.value.push(
					graph.value
						.activeChart()
						.createShape(
							{ time: order.createdAt.getTime(), price: order.value },
							defaultOrderShape(order, color)
						)
				);
			}
		}
	});
}

function defaultOrderShape(
	order,
	color,
	price = null
): CreateShapeOptions<any> {
	let text = orderInitials[order.method];
	if (order.method == 3 && price) {
		text = "TSL | Trigger";
	}
	return {
		shape: "horizontal_line",
		text: `${text} | ${formatPrice(
			price || order.triggerPrice || order.value
		)}`,
		lock: true,
		overrides: {
			showLabel: true,
			horzLabelsAlign: "right",
			linestyle: 1,
			linecolor: color,
			textcolor: color,
		},
	};
}

function defaultAvgBuyShape(price = null): CreateShapeOptions<any> {
	return {
		shape: "horizontal_line",
		text: `Avg Buy | ${formatPrice(price)}`,
		lock: true,
		overrides: {
			showLabel: true,
			horzLabelsAlign: "right",
			linestyle: 4,
			linewidth: 1,
			linecolor: "green",
			textcolor: "grey",
		},
	};
}

function setLastTrade() {
	if (
		walletStore.isConnected &&
		props.trades[0]?.signer === walletStore.address.toString()
	) {
		addWalletTrade();
	}

	if (props.token?.createdBy?.toString() === props.trades[0]?.signer) {
		addDevTrade();
	}
}

function addWalletTrade() {
	const marks = setTradeMarks([props.trades[0]]);
	currentMarks.value = marks;
	updateMarksCallback.value(marks);
}

function addDevTrade() {
	const mark = _buildEventMarkFromTrade(props.trades[0]);
	if (!mark) return;

	currentMarks.value = (currentMarks.value || []).concat([mark]);
	updateMarksCallback.value(currentMarks.value);
}

// setup
onMounted(() => {
	init();
	setOrdersOnGraph();
	setAvgBuyPrice();
});

onBeforeUnmount(() => {
	inspectorChannel.close();
	clearInterval(interval);
});

// watch
watch(
	() => props.trades,
	() => {
		setLastTrade();
		setArrowsOnGraph();
	}
);
watch(
	() => props.orders,
	() => {
		setOrdersOnGraph();
	}
);
watch(
	() => props.pnl,
	() => {
		setAvgBuyPrice();
	}
);
watch(
	() => props.latestCandle,
	() => {
		updateBarsCallback.value(props.latestCandle);
	}
);

// emits


// expose
expose({ bars: allBars.value });

return { __sfc: true,props, walletStore, interval, config, markHistory, currentMarks, graph, updateBarsCallback, updateMarksCallback, arrowMarkID, avgBuyID, orderColors, orderInitials, allBars, lastResolution, ordersID, inspectorChannel, chartReady, opts, init, getDataFeed, getHistoricDevTradeMarks, getHistoricTradeMarks, setTradeMarks, _buildTradeMarks, _buildEventMarks, _buildEventMarkFromTrade, setArrowsOnGraph, intervalToMilliSeconds, setAvgBuyPrice, setOrdersOnGraph, defaultOrderShape, defaultAvgBuyShape, setLastTrade, addWalletTrade, addDevTrade, emit }
}

})